type QueryPathToken =
  | { type: 'prop'; key: string }
  | { type: 'index'; index: number }
  | { type: 'append' };

function parseQueryValueBySchema(rawValue: string, fieldSchema: any): any {
  const fieldType = fieldSchema?.type;

  if (fieldType === 'boolean') {
    const normalized = rawValue.trim().toLowerCase();
    if (normalized === 'true') {
      return true;
    }
    if (normalized === 'false') {
      return false;
    }
    return rawValue;
  }

  if (fieldType === 'integer' || fieldType === 'number') {
    const parsed = Number(rawValue);
    if (!Number.isNaN(parsed)) {
      return fieldType === 'integer' ? Math.trunc(parsed) : parsed;
    }
    return rawValue;
  }

  return rawValue;
}

function parseQueryPath(path: string): QueryPathToken[] | null {
  if (!path) {
    return null;
  }

  const tokens: QueryPathToken[] = [];
  const parts = path.split('.').filter(Boolean);

  if (!parts.length) {
    return null;
  }

  for (const part of parts) {
    const baseMatch = part.match(/^([^\[\]]+)/);
    const baseKey = baseMatch?.[1];
    if (!baseKey) {
      return null;
    }

    tokens.push({ type: 'prop', key: baseKey });

    const bracketPart = part.slice(baseKey.length);
    if (!bracketPart) {
      continue;
    }

    const bracketRegex = /\[([^\]]*)\]/g;
    let consumed = '';
    let match: RegExpExecArray | null;
    while ((match = bracketRegex.exec(bracketPart)) !== null) {
      consumed += match[0];
      const content = match[1];
      if (content === '') {
        tokens.push({ type: 'append' });
        continue;
      }

      if (!/^\d+$/.test(content)) {
        return null;
      }

      tokens.push({ type: 'index', index: Number(content) });
    }

    if (consumed !== bracketPart) {
      return null;
    }
  }

  return tokens;
}

function resolveLeafSchemaForQueryPath(schema: any, tokens: QueryPathToken[]): any {
  let currentSchema = schema;

  for (const token of tokens) {
    if (!currentSchema) {
      return undefined;
    }

    if (token.type === 'prop') {
      currentSchema = currentSchema?.properties?.[token.key];
      continue;
    }

    currentSchema = currentSchema?.items;
  }

  return currentSchema;
}

function getContainerFromNextToken(nextToken: QueryPathToken | undefined): any {
  if (!nextToken) {
    return {};
  }

  return nextToken.type === 'prop' ? {} : [];
}

function applyQueryValuesByPath(target: any, tokens: QueryPathToken[], rawValues: string[], rootSchema: any): void {
  if (!tokens.length || !rawValues.length) {
    return;
  }

  const appendTokenIndex = tokens.findIndex((token) => token.type === 'append');
  if (appendTokenIndex !== -1 && appendTokenIndex !== tokens.length - 1) {
    // Keep syntax simple and predictable: [] is only supported as final path element.
    return;
  }

  const leafSchema = resolveLeafSchemaForQueryPath(rootSchema, tokens);
  let current: any = target;

  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i];
    const isLast = i === tokens.length - 1;
    const nextToken = tokens[i + 1];

    if (token.type === 'prop') {
      if (typeof current !== 'object' || current === null || Array.isArray(current)) {
        return;
      }

      if (isLast) {
        current[token.key] = parseQueryValueBySchema(rawValues[rawValues.length - 1], leafSchema);
        return;
      }

      if (current[token.key] === undefined || current[token.key] === null) {
        current[token.key] = getContainerFromNextToken(nextToken);
      }

      current = current[token.key];
      continue;
    }

    if (token.type === 'index') {
      if (!Array.isArray(current)) {
        return;
      }

      if (isLast) {
        current[token.index] = parseQueryValueBySchema(rawValues[rawValues.length - 1], leafSchema);
        return;
      }

      if (current[token.index] === undefined || current[token.index] === null) {
        current[token.index] = getContainerFromNextToken(nextToken);
      }

      current = current[token.index];
      continue;
    }

    if (!Array.isArray(current)) {
      return;
    }

    rawValues.forEach((rawValue) => {
      current.push(parseQueryValueBySchema(rawValue, leafSchema));
    });
    return;
  }
}

export function buildFormDataFromQueryParams(schema: any, search: string): any {
  if (!search) {
    return {};
  }

  const queryParams = new URLSearchParams(search);
  const queryFormData: any = {};
  const uniqueKeys = Array.from(new Set(Array.from(queryParams.keys())));

  uniqueKeys.forEach((key) => {
    const values = queryParams.getAll(key);
    if (!values.length) {
      return;
    }

    const tokens = parseQueryPath(key);
    if (!tokens) {
      return;
    }

    applyQueryValuesByPath(queryFormData, tokens, values, schema);
  });

  return queryFormData;
}
